/**
 * Simulation inactivity confirmation dialog.
 *
 * Prompts learners to confirm they are still present after inactivity.
 *
 * @module SimulationInactivityDialog
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface SimulationInactivityDialogProps {
  readonly open: boolean;
  readonly timeRemainingSeconds: number | null;
  readonly onContinue: () => void;
  readonly onEnd: () => void;
}

function formatRemaining(totalSeconds: number, locale: string): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return new Intl.NumberFormat(locale, { minimumIntegerDigits: 2 }).format(minutes)
    + ':'
    + new Intl.NumberFormat(locale, { minimumIntegerDigits: 2 }).format(seconds);
}

export function SimulationInactivityDialog({
  open,
  timeRemainingSeconds,
  onContinue,
  onEnd,
}: SimulationInactivityDialogProps) {
  const { t, i18n } = useTranslation();

  const remainingLabel = useMemo(() => {
    if (typeof timeRemainingSeconds !== 'number' || timeRemainingSeconds < 0) {
      return t('simulation.inactivity.unknownTime', { defaultValue: 'Unknown' });
    }
    return formatRemaining(timeRemainingSeconds, i18n.language);
  }, [i18n.language, t, timeRemainingSeconds]);

  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        onContinue();
      }
    }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('simulation.inactivity.title', { defaultValue: 'Still there?' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('simulation.inactivity.description', {
              defaultValue: 'You have been inactive for a while. Confirm to continue or end this simulation.',
            })}
          </AlertDialogDescription>
          <p className="text-sm font-medium text-foreground mt-2">
            {t('simulation.inactivity.timeRemaining', {
              defaultValue: 'Time remaining: {{time}}',
              time: remainingLabel,
            })}
          </p>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onEnd}>
            {t('simulation.inactivity.end', { defaultValue: 'End' })}
          </AlertDialogCancel>
          <AlertDialogAction onClick={onContinue}>
            {t('simulation.inactivity.continue', { defaultValue: 'Continue' })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
